import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import TextField from "../../components/frontend/TextField";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaPaperPlane,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp
} from "react-icons/fa";
import SEOHead from "../common/SEOHead";
import { applyStoreTitle } from "../../util/storeLogoHelper";
import { loadStoreSettings } from "../../util/storeSettings";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [storeSettings, setStoreSettings] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    applyStoreTitle(null, "Contact Us");

    let ismounted = true;
    loadStoreSettings()
      .then((settings) => {
        if (ismounted && settings && (settings.storeName || settings.logoUrl)) {
          setStoreSettings(settings);
          applyStoreTitle(settings, "Contact Us");
        }
      })
      .catch(() => {});

    return () => {
      ismounted = false;
    };
  }, []);

  const validate = Yup.object({
    fullname: Yup.string().max(40, "Must be 40 characters or less").required("Full name is required"),
    email: Yup.string().email("Please enter a valid email").required("Email is required"),
    message: Yup.string()
      .min(10, "Message must be at least 10 characters")
      .max(500, "Message must be less than 500 characters")
      .required("Message is required"),
  });

  return (
    <div>
      <SEOHead
        title="Contact Us & Support"
        description={`Get in touch with the ${storeSettings?.storeName || "Gootocart"} team for order inquiries, delivery assistance, and customer support.`}
        url="/contact"
        type="website"
      />

      {/* Header Banner & Breadcrumbs */}
      <div className="page-header-modern">
        <div className="container">
          <ul className="breadcrumb-modern">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="active">Contact Us</li>
          </ul>
          <h1 className="page-header-title">Get in Touch With Our Team</h1>
        </div>
      </div>

      <div className="container py-4">
        <div className="row g-5">
          {/* Left: Contact Form */}
          <div className="col-12 col-lg-7">
            <div className="cart-table-card p-4 p-md-5">
              <span
                className="badge px-3 py-2 mb-2"
                style={{ background: "var(--accent-light)", color: "var(--accent)", fontWeight: 700 }}
              >
                Send a Message
              </span>
              <h3 className="fw-bold mb-2">We'd love to hear from you</h3>
              <p className="text-muted mb-4">
                Have a question regarding your order, bulk inquiries, or product feedback? Fill out the form below and we'll reply within 24 hours.
              </p>

              <Formik
                initialValues={{
                  fullname: "",
                  email: "",
                  message: "",
                }}
                validationSchema={validate}
                onSubmit={(values, { resetForm }) => {
                  setSent(true);
                  resetForm();
                }}
              >
                {() => (
                  <Form onChange={() => sent && setSent(false)}>
                    {sent && (
                      <div className="alert alert-success py-2 px-3 small" role="status">
                        Thank you for reaching out. We will get back to you shortly.
                      </div>
                    )}
                    <TextField
                      label="Your Full Name"
                      type="text"
                      name="fullname"
                      placeholder="e.g. Ali Raza"
                    />

                    <TextField
                      label="Email Address"
                      type="email"
                      name="email"
                      placeholder="name@example.com"
                    />

                    <div className="mb-4">
                      <label className="form-label fw-semibold" style={{ fontSize: "13.5px" }}>
                        Your Message
                      </label>
                      <TextField
                        type="text"
                        name="message"
                        placeholder="How can we assist you?"
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary px-4 py-3 fw-bold rounded-pill d-inline-flex align-items-center gap-2"
                      style={{ background: "var(--primary)", border: "none" }}
                    >
                      <FaPaperPlane size={13} /> Send Message
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </div>

          {/* Right: Contact Information Cards */}
          <div className="col-12 col-lg-5">
            <div className="d-flex flex-column gap-3">
              {/* Address Card */}
              <div className="p-4 bg-white rounded-3 border d-flex gap-3 align-items-start" style={{ boxShadow: "var(--shadow-xs)" }}>
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "var(--radius-md)",
                    background: "var(--accent-light)",
                    color: "var(--accent)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    flexShrink: 0,
                  }}
                >
                  <FaMapMarkerAlt />
                </div>
                <div>
                  <h6 className="fw-bold mb-1">Store Location</h6>
                  <p className="m-0 text-muted" style={{ fontSize: "13.5px" }}>
                    {storeSettings?.warehouseAddress ||
                      "Shop G21, KN Gohar Society, Behind Malir Courts, Karachi, Pakistan"}
                  </p>
                </div>
              </div>

              {/* Phone Card */}
              <div className="p-4 bg-white rounded-3 border d-flex gap-3 align-items-start" style={{ boxShadow: "var(--shadow-xs)" }}>
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "var(--radius-md)",
                    background: "var(--success-light)",
                    color: "var(--success)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    flexShrink: 0,
                  }}
                >
                  <FaPhoneAlt />
                </div>
                <div>
                  <h6 className="fw-bold mb-1">Direct Phone / WhatsApp</h6>
                  <p className="m-0 text-muted" style={{ fontSize: "13.5px" }}>
                    {storeSettings?.supportPhone || "0332-2417001"} ({storeSettings?.supportHours || "Mon–Sat, 10 AM – 8 PM"})
                  </p>
                </div>
              </div>

              {storeSettings?.supportEmail ? (
              <div className="p-4 bg-white rounded-3 border d-flex gap-3 align-items-start" style={{ boxShadow: "var(--shadow-xs)" }}>
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "var(--radius-md)",
                    background: "var(--accent-gold-light)",
                    color: "var(--accent-gold)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    flexShrink: 0,
                  }}
                >
                  <FaEnvelope />
                </div>
                <div>
                  <h6 className="fw-bold mb-1">Customer Support Email</h6>
                  <p className="m-0 text-muted" style={{ fontSize: "13.5px" }}>
                    {storeSettings.supportEmail}
                  </p>
                </div>
              </div>
              ) : null}

              {/* Hours Card */}
              <div className="p-4 bg-white rounded-3 border d-flex gap-3 align-items-start" style={{ boxShadow: "var(--shadow-xs)" }}>
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "var(--radius-md)",
                    background: "var(--primary-light)",
                    color: "var(--primary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    flexShrink: 0,
                  }}
                >
                  <FaClock />
                </div>
                <div>
                  <h6 className="fw-bold mb-1">Operating Hours</h6>
                  <p className="m-0 text-muted" style={{ fontSize: "13.5px" }}>
                    {storeSettings?.supportHours || "Monday – Saturday: 10:00 AM – 8:00 PM"} <br />
                    Sunday: Closed
                  </p>
                </div>
              </div>

              {/* Social Links Card */}
              <div className="p-4 bg-white rounded-3 border" style={{ boxShadow: "var(--shadow-xs)" }}>
                <h6 className="fw-bold mb-3">Connect On Social Media</h6>
                <div className="d-flex gap-2">
                  {storeSettings?.facebookUrl && (
                    <a
                      href={storeSettings.facebookUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="nav-action-btn"
                      aria-label="Facebook"
                    >
                      <FaFacebookF size={14} />
                    </a>
                  )}
                  {storeSettings?.twitterUrl && (
                    <a
                      href={storeSettings.twitterUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="nav-action-btn"
                      aria-label="Twitter"
                    >
                      <FaTwitter size={14} />
                    </a>
                  )}
                  {storeSettings?.instagramUrl && (
                    <a
                      href={storeSettings.instagramUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="nav-action-btn"
                      aria-label="Instagram"
                    >
                      <FaInstagram size={14} />
                    </a>
                  )}
                  {storeSettings?.linkedinUrl && (
                    <a
                      href={storeSettings.linkedinUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="nav-action-btn"
                      aria-label="LinkedIn"
                    >
                      <FaLinkedinIn size={14} />
                    </a>
                  )}
                  {storeSettings?.whatsappNumber && (
                    <a
                      href={
                        storeSettings.whatsappNumber.startsWith("http")
                          ? storeSettings.whatsappNumber
                          : `https://wa.me/${storeSettings.whatsappNumber.replace(/[^0-9]/g, "")}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="nav-action-btn"
                      style={{ color: "#25D366" }}
                      aria-label="WhatsApp"
                    >
                      <FaWhatsapp size={14} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
